import { Button } from '@accure/ui';
import * as Sentry from '@sentry/react';
import { z } from 'zod';

import { FullPageContent } from '@/components/layout/full-page-content';
import { Loading } from '@/components/ui/old/Loading';
import { SlideOver } from '@/components/ui/slide-over';
import { UserRole } from '@/entities/user-role';
import { codec, useQueryParams } from '@/hooks/router/use-query-params';
import { useMe } from '@/hooks/use-me';

import { CreateTaskComment } from '../api/mutations/create-task-comment';
import { DeleteTaskComment } from '../api/mutations/delete-task-comment';
import { UpdateTask } from '../api/mutations/update-task';
import { UpdateTaskComment } from '../api/mutations/update-task-comment';
import { GetTask } from '../api/queries/get-task';
import { ListTasks } from '../api/queries/list-tasks';
import { TaskBoardActions } from '../components/task-board-actions';
import { TaskDetails } from '../components/task-details';
import { TasksBoard } from '../components/tasks-board';
import { TasksFilters } from '../components/tasks-filters';

export function TasksPage() {
  const me = useMe();
  const [{ id }, queryParams] = useQueryParams({
    id: {
      key: 'id',
      schema: z.string().optional(),
      codec: codec.string,
    },
  });
  const [filters, setFilters] = ListTasks.useGetFiltersFromQueryParams();

  function openDetails(id: string) {
    queryParams.replace({ id });
  }

  function closeDetails() {
    queryParams.replace({ id: undefined });
  }

  return (
    <>
      <FullPageContent
        className="relative grid grid-rows-[min-content_1fr]"
        testId="tasks-page"
      >
        <div className="flex w-full flex-row items-end justify-between gap-4 border-b p-4">
          <TasksFilters
            filters={filters}
            onChange={(filters) => setFilters.push(filters)}
          />
          {me.role !== UserRole.THIRD_PARTY ? <TaskBoardActions /> : null}
        </div>
        <TasksBoard filters={filters} onTaskOpen={openDetails} />
      </FullPageContent>
      <SlideOver open={Boolean(id)} onClose={closeDetails}>
        {id ? <TaskDetailsPage id={id} onClose={closeDetails} /> : null}
      </SlideOver>
    </>
  );
}

type TaskDetailsPageProps = { id: string; onClose: () => void };

export function TaskDetailsPage({ id, onClose }: TaskDetailsPageProps) {
  const getTask = GetTask.useQuery({ id }, { refetchInterval: 10_000 });
  const updateTask = UpdateTask.useMutation();
  const createComment = CreateTaskComment.useMutation();
  const deleteComment = DeleteTaskComment.useMutation();
  const updateComment = UpdateTaskComment.useMutation();

  if (getTask.isLoading) {
    return <Loading />;
  }

  if (getTask.isError) {
    Sentry.captureException(getTask.error);
    return (
      <div className="flex h-full flex-col items-center justify-center gap-4 p-8 text-center">
        <div className="text-lg font-semibold text-gray-900">
          Unable to load task details
        </div>
        <div className="max-w-md text-sm text-gray-600">
          {getTask.error instanceof Error
            ? getTask.error.message
            : 'An unexpected error occurred. Please try again.'}
        </div>
        <Button onClick={() => getTask.refetch()} variant="primaryOutline">
          Retry
        </Button>
      </div>
    );
  }

  if (!getTask.data) {
    return null;
  }

  return (
    <TaskDetails
      task={getTask.data.task}
      onTaskCommentCreate={(input) => createComment.mutateAsync(input)}
      onTaskCommentUpdate={(input) => updateComment.mutateAsync(input)}
      onTaskCommentDelete={(input) => deleteComment.mutateAsync(input)}
      onTaskUpdate={(input) => updateTask.mutateAsync(input)}
      onClose={onClose}
    />
  );
}
